import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Tooltip, Paper, Typography } from '@mui/material';

interface SiteMapCanvasProps {
  mapData: any;
  zoom: number;
  pan: { x: number; y: number };
  setPan: (pan: { x: number; y: number }) => void;
  showGrid: boolean;
  snapToGrid: boolean;
  showLayers: {
    sites: boolean;
    features: boolean;
    zones: boolean;
    labels: boolean;
  };
  selectedSites: Set<string>;
  hoveredSite: string | null;
  setHoveredSite: (siteId: string | null) => void;
  onSiteClick: (siteId: string) => void;
  onSitePositionUpdate: (siteId: string, position: { x: number; y: number }) => void;
  editMode: 'view' | 'edit' | 'add';
}

const SiteMapCanvas: React.FC<SiteMapCanvasProps> = ({
  mapData,
  zoom,
  pan,
  setPan,
  showGrid,
  snapToGrid,
  showLayers,
  selectedSites,
  hoveredSite,
  setHoveredSite,
  onSiteClick,
  onSitePositionUpdate,
  editMode
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [draggedSite, setDraggedSite] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipContent, setTooltipContent] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  // Grid settings
  const gridSize = mapData?.layout?.gridSize || 20;

  // Draw the map
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !mapData) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context state
    ctx.save();

    // Apply zoom and pan
    ctx.translate(pan.x, pan.y);
    ctx.scale(zoom, zoom);

    // Draw background
    ctx.fillStyle = mapData.layout?.backgroundColor || '#f5f5f5';
    ctx.fillRect(0, 0, mapData.dimensions.width, mapData.dimensions.height);

    // Draw grid
    if (showGrid) {
      drawGrid(ctx);
    }

    // Draw zones
    if (showLayers.zones && mapData.zones) {
      drawZones(ctx);
    }

    // Draw features
    if (showLayers.features && mapData.features) {
      drawFeatures(ctx);
    }

    // Draw sites
    if (showLayers.sites && mapData.sites) {
      drawSites(ctx);
    }

    // Restore context state
    ctx.restore();
  }, [mapData, zoom, pan, showGrid, showLayers, selectedSites, hoveredSite]);

  // Draw grid
  const drawGrid = (ctx: CanvasRenderingContext2D) => {
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.lineWidth = 0.5;

    // Vertical lines
    for (let x = 0; x <= mapData.dimensions.width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, mapData.dimensions.height);
      ctx.stroke();
    }

    // Horizontal lines
    for (let y = 0; y <= mapData.dimensions.height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(mapData.dimensions.width, y);
      ctx.stroke();
    }
  };

  // Draw zones
  const drawZones = (ctx: CanvasRenderingContext2D) => {
    mapData.zones.forEach((zone: any) => {
      ctx.save();

      // Set zone styling
      ctx.fillStyle = zone.styling?.fillColor || 'rgba(0, 0, 0, 0.1)';
      ctx.globalAlpha = zone.styling?.fillOpacity || 0.2;
      ctx.strokeStyle = zone.styling?.strokeColor || '#333';
      ctx.lineWidth = zone.styling?.strokeWidth || 2;

      if (zone.styling?.strokeDasharray) {
        const dashArray = zone.styling.strokeDasharray.split(',').map(Number);
        ctx.setLineDash(dashArray);
      }

      // Draw zone boundary
      if (zone.boundary.type === 'rectangle') {
        const bounds = zone.boundary.rectangleBounds;
        ctx.fillRect(bounds.x, bounds.y, bounds.width, bounds.height);
        ctx.strokeRect(bounds.x, bounds.y, bounds.width, bounds.height);
      } else if (zone.boundary.type === 'polygon') {
        ctx.beginPath();
        zone.boundary.polygonPoints.forEach((point: any, index: number) => {
          if (index === 0) {
            ctx.moveTo(point.x, point.y);
          } else {
            ctx.lineTo(point.x, point.y);
          }
        });
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }

      // Draw zone label
      if (showLayers.labels && zone.label) {
        ctx.globalAlpha = 1;
        ctx.fillStyle = zone.label.color || '#333';
        ctx.font = `${zone.label.fontSize || 16}px Arial`;
        ctx.fillText(zone.label.text, zone.label.position.x, zone.label.position.y);
      }

      ctx.restore();
    });
  };

  // Draw features
  const drawFeatures = (ctx: CanvasRenderingContext2D) => {
    mapData.features.forEach((feature: any) => {
      ctx.save();

      // Set feature styling
      ctx.fillStyle = feature.styling?.fillColor || '#999';
      ctx.strokeStyle = feature.styling?.strokeColor || '#333';
      ctx.lineWidth = feature.styling?.strokeWidth || 2;
      ctx.globalAlpha = feature.styling?.opacity || 1;

      // Draw feature shape
      switch (feature.shape) {
        case 'rectangle':
          ctx.fillRect(
            feature.position.x,
            feature.position.y,
            feature.dimensions.width,
            feature.dimensions.height
          );
          ctx.strokeRect(
            feature.position.x,
            feature.position.y,
            feature.dimensions.width,
            feature.dimensions.height
          );
          break;
        
        case 'circle':
          ctx.beginPath();
          ctx.arc(
            feature.position.x + feature.dimensions.width / 2,
            feature.position.y + feature.dimensions.height / 2,
            Math.min(feature.dimensions.width, feature.dimensions.height) / 2,
            0,
            Math.PI * 2
          );
          ctx.fill();
          ctx.stroke();
          break;
        
        case 'polygon':
          ctx.beginPath();
          feature.polygonPoints.forEach((point: any, index: number) => {
            if (index === 0) {
              ctx.moveTo(point.x + feature.position.x, point.y + feature.position.y);
            } else {
              ctx.lineTo(point.x + feature.position.x, point.y + feature.position.y);
            }
          });
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          break;
      }

      // Draw feature label
      if (showLayers.labels && feature.label) {
        ctx.fillStyle = feature.label.color || '#fff';
        ctx.font = `${feature.label.fontSize || 12}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(
          feature.label.text,
          feature.position.x + feature.dimensions.width / 2,
          feature.position.y + feature.dimensions.height / 2
        );
      }

      ctx.restore();
    });
  };

  // Draw sites
  const drawSites = (ctx: CanvasRenderingContext2D) => {
    mapData.sites.forEach((siteData: any) => {
      const site = siteData.site;
      const isSelected = selectedSites.has(site._id);
      const isHovered = hoveredSite === site._id;
      const isDragged = draggedSite === site._id;

      ctx.save();

      // Apply rotation if any
      if (siteData.rotation) {
        const centerX = siteData.position.x + siteData.dimensions.width / 2;
        const centerY = siteData.position.y + siteData.dimensions.height / 2;
        ctx.translate(centerX, centerY);
        ctx.rotate((siteData.rotation * Math.PI) / 180);
        ctx.translate(-centerX, -centerY);
      }

      // Set site styling
      let fillColor = siteData.styling?.fillColor || '#4caf50';
      if (isSelected && editMode === 'edit') {
        fillColor = '#2196f3';
      } else if (isHovered) {
        fillColor = siteData.styling?.hoverEffect?.fillColor || '#45a049';
      }

      ctx.fillStyle = fillColor;
      ctx.strokeStyle = isSelected ? '#1976d2' : (siteData.styling?.strokeColor || '#333');
      ctx.lineWidth = isSelected ? 3 : (siteData.styling?.strokeWidth || 2);
      ctx.globalAlpha = isDragged ? 0.7 : (siteData.styling?.opacity || 1);

      // Draw site shape
      switch (siteData.shape) {
        case 'rectangle':
        default:
          ctx.fillRect(
            siteData.position.x,
            siteData.position.y,
            siteData.dimensions.width,
            siteData.dimensions.height
          );
          ctx.strokeRect(
            siteData.position.x,
            siteData.position.y,
            siteData.dimensions.width,
            siteData.dimensions.height
          );
          break;
        
        case 'polygon':
          ctx.beginPath();
          siteData.polygonPoints.forEach((point: any, index: number) => {
            if (index === 0) {
              ctx.moveTo(point.x + siteData.position.x, point.y + siteData.position.y);
            } else {
              ctx.lineTo(point.x + siteData.position.x, point.y + siteData.position.y);
            }
          });
          ctx.closePath();
          ctx.fill();
          ctx.stroke();
          break;
      }

      // Draw site labels
      if (showLayers.labels && siteData.labels) {
        ctx.globalAlpha = 1;
        
        // Primary label (site number)
        if (siteData.labels.primary) {
          ctx.fillStyle = siteData.labels.primary.color || '#fff';
          ctx.font = `${siteData.labels.primary.fontWeight || 'bold'} ${siteData.labels.primary.fontSize || 14}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          let labelY = siteData.position.y + siteData.dimensions.height / 2;
          if (siteData.labels.primary.position === 'top') {
            labelY = siteData.position.y + 15;
          } else if (siteData.labels.primary.position === 'bottom') {
            labelY = siteData.position.y + siteData.dimensions.height - 15;
          }
          
          ctx.fillText(
            siteData.labels.primary.text || site.siteNumber,
            siteData.position.x + siteData.dimensions.width / 2,
            labelY
          );
        }
        
        // Secondary label (price)
        if (siteData.labels.secondary) {
          ctx.fillStyle = siteData.labels.secondary.color || '#fff';
          ctx.font = `${siteData.labels.secondary.fontSize || 12}px Arial`;
          ctx.fillText(
            siteData.labels.secondary.text || `$${site.pricePerNight}`,
            siteData.position.x + siteData.dimensions.width / 2,
            siteData.position.y + siteData.dimensions.height / 2 + 15
          );
        }
      }

      // Draw amenity icons
      if (siteData.amenityIcons) {
        siteData.amenityIcons.forEach((icon: any, index: number) => {
          const iconSize = 16;
          const iconMargin = 4;
          let iconX = siteData.position.x + iconMargin;
          let iconY = siteData.position.y + iconMargin;
          
          switch (icon.position) {
            case 'top-right':
              iconX = siteData.position.x + siteData.dimensions.width - iconSize - iconMargin;
              break;
            case 'bottom-left':
              iconY = siteData.position.y + siteData.dimensions.height - iconSize - iconMargin;
              break;
            case 'bottom-right':
              iconX = siteData.position.x + siteData.dimensions.width - iconSize - iconMargin;
              iconY = siteData.position.y + siteData.dimensions.height - iconSize - iconMargin;
              break;
          }
          
          // Draw icon background
          ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
          ctx.fillRect(iconX, iconY, iconSize, iconSize);
          
          // Draw icon (simplified representation)
          ctx.fillStyle = '#333';
          ctx.font = '12px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          const iconSymbols: { [key: string]: string } = {
            'electric': '⚡',
            'water': '💧',
            'sewer': '🚿',
            'wifi': '📶',
            'shade': '🌳',
            'pet': '🐕',
            'firepit': '🔥'
          };
          ctx.fillText(
            iconSymbols[icon.type] || '?',
            iconX + iconSize / 2,
            iconY + iconSize / 2
          );
        });
      }

      ctx.restore();
    });
  };

  // Handle mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    // Check if clicking on a site
    const clickedSite = findSiteAtPosition(x, y);
    
    if (clickedSite && editMode === 'edit') {
      setDraggedSite(clickedSite.site._id);
      setDragOffset({
        x: x - clickedSite.position.x,
        y: y - clickedSite.position.y
      });
    } else {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    // Handle site dragging
    if (draggedSite && editMode === 'edit') {
      let newX = x - dragOffset.x;
      let newY = y - dragOffset.y;
      
      // Snap to grid if enabled
      if (snapToGrid) {
        newX = Math.round(newX / gridSize) * gridSize;
        newY = Math.round(newY / gridSize) * gridSize;
      }
      
      onSitePositionUpdate(draggedSite, { x: newX, y: newY });
      return;
    }

    // Handle map panning
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
      return;
    }

    // Check hover
    const hoveredSiteData = findSiteAtPosition(x, y);
    if (hoveredSiteData) {
      setHoveredSite(hoveredSiteData.site._id);
      setTooltipContent(`${hoveredSiteData.site.siteNumber} - ${hoveredSiteData.site.type}`);
      setTooltipPosition({ x: e.clientX, y: e.clientY });
      setShowTooltip(true);
    } else {
      setHoveredSite(null);
      setShowTooltip(false);
    }
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (draggedSite) {
      setDraggedSite(null);
      return;
    }

    if (!isDragging) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = (e.clientX - rect.left - pan.x) / zoom;
      const y = (e.clientY - rect.top - pan.y) / zoom;

      const clickedSite = findSiteAtPosition(x, y);
      if (clickedSite) {
        onSiteClick(clickedSite.site._id);
      }
    }
    
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setHoveredSite(null);
    setShowTooltip(false);
    setIsDragging(false);
    setDraggedSite(null);
  };

  // Find site at position
  const findSiteAtPosition = (x: number, y: number) => {
    if (!mapData?.sites) return null;

    for (const siteData of mapData.sites) {
      if (
        x >= siteData.position.x &&
        x <= siteData.position.x + siteData.dimensions.width &&
        y >= siteData.position.y &&
        y <= siteData.position.y + siteData.dimensions.height
      ) {
        return siteData;
      }
    }
    return null;
  };

  // Handle wheel zoom
  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.5, Math.min(2, zoom * delta));
    
    // Zoom towards mouse position
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) {
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      // Calculate new pan to keep mouse position stable
      const scaleDiff = newZoom - zoom;
      setPan({
        x: pan.x - (mouseX - pan.x) * (scaleDiff / zoom),
        y: pan.y - (mouseY - pan.y) * (scaleDiff / zoom)
      });
    }
  };

  // Set canvas size
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const updateCanvasSize = () => {
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      draw();
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, []);

  // Redraw on changes
  useEffect(() => {
    draw();
  }, [draw]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: 'calc(100% - 64px)',
        overflow: 'hidden',
        cursor: isDragging ? 'grabbing' : draggedSite ? 'move' : 'grab'
      }}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
        style={{
          display: 'block',
          cursor: hoveredSite ? 'pointer' : 'inherit'
        }}
      />
      
      {showTooltip && (
        <Paper
          sx={{
            position: 'fixed',
            left: tooltipPosition.x + 10,
            top: tooltipPosition.y - 30,
            padding: '4px 8px',
            pointerEvents: 'none',
            zIndex: 1000
          }}
          elevation={3}
        >
          <Typography variant="caption">{tooltipContent}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SiteMapCanvas;